import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError } from "@/api/client";
import type { AuthResult } from "@/api/types";
import { useSession } from "@/app/session";
import { useRouter, Link } from "@/app/router";
import { Button, Field, Input } from "@/components/ui";
import { useToast, errorMessage } from "@/components/Toast";

function AuthShell({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2.5">
          <span className="size-3 rounded-full bg-running record-live" />
          <span className="text-lg font-semibold tracking-tight">Timer</span>
        </div>
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-1 mb-6 text-sm text-muted">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

function fieldErrors(err: unknown): Record<string, string> {
  if (err instanceof ApiError && err.fields) return err.fields;
  return {};
}

export function LoginPage() {
  const { login } = useSession();
  const { navigate } = useRouter();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [notActivated, setNotActivated] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNotActivated(false);
    try {
      await login(email, password);
      navigate("/tracker", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "not_activated") {
        setNotActivated(true);
      } else {
        toast(errorMessage(err), "error");
      }
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    try {
      await api.post("/auth/resend-activation", { email });
      toast("Activation e-mail sent — check your inbox", "success");
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to keep tracking.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="E-mail">
          <Input type="email" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
        <Field label="Password">
          <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </Field>
        {notActivated && (
          <p className="text-sm text-warn">
            Your account isn't activated.{" "}
            <button type="button" onClick={resend} className="font-medium text-accent underline underline-offset-2">
              Resend e-mail
            </button>
          </p>
        )}
        <Button type="submit" loading={busy} className="w-full">
          Sign in
        </Button>
      </form>
      <div className="mt-5 flex items-center justify-between text-sm text-muted">
        <Link to="/register" className="hover:text-ink">
          Create account
        </Link>
        <Link to="/reset-password" className="hover:text-ink">
          Forgot password?
        </Link>
      </div>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await api.post("/auth/register", { name, email, password });
      setSent(true);
    } catch (err) {
      setErrors(fieldErrors(err));
      if (!(err instanceof ApiError) || !err.fields) toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthShell title="Check your e-mail" subtitle={`We sent an activation link to ${email}.`}>
        <p className="text-sm text-muted">
          Click the link to activate your account. The link expires in 24 hours.
        </p>
        <Link to="/login" className="mt-6 inline-block text-sm font-medium text-accent hover:underline">
          Back to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create your account" subtitle="Track time in seconds, not spreadsheets.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name" error={errors.name}>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
        </Field>
        <Field label="E-mail" error={errors.email}>
          <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
        <Field label="Password" error={errors.password} hint="At least 10 characters.">
          <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </Field>
        <Button type="submit" loading={busy} className="w-full">
          Create account
        </Button>
      </form>
      <Link to="/login" className="mt-5 inline-block text-sm text-muted hover:text-ink">
        Already have an account? Sign in
      </Link>
    </AuthShell>
  );
}

export function ActivatePage() {
  const { applyAuth } = useSession();
  const { navigate } = useRouter();
  const [state, setState] = useState<"working" | "error">("working");

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token") ?? "";
    if (!token) {
      setState("error");
      return;
    }
    api
      .post<AuthResult>("/auth/activate", { token })
      .then(async (res) => {
        await applyAuth(res);
        navigate("/tracker", { replace: true });
      })
      .catch(() => setState("error"));
  }, [applyAuth, navigate]);

  return (
    <AuthShell
      title={state === "working" ? "Activating…" : "Link expired"}
      subtitle={state === "working" ? "One moment while we set up your workspace." : "This activation link is invalid or has already been used."}
    >
      {state === "error" && (
        <Link to="/login" className="text-sm font-medium text-accent hover:underline">
          Back to sign in
        </Link>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const { toast } = useToast();
  const { navigate } = useRouter();
  const token = new URLSearchParams(window.location.search).get("token");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function requestReset(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function setNewPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await api.post("/auth/reset-password", { token, password });
      toast("Password reset — you can sign in now", "success");
      navigate("/login", { replace: true });
    } catch (err) {
      setErrors(fieldErrors(err));
      if (!(err instanceof ApiError) || !err.fields) toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  if (token) {
    return (
      <AuthShell title="Choose a new password" subtitle="Make it at least 10 characters.">
        <form onSubmit={setNewPassword} className="space-y-4">
          <Field label="New password" error={errors.password}>
            <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </Field>
          <Button type="submit" loading={busy} className="w-full">
            Reset password
          </Button>
        </form>
      </AuthShell>
    );
  }

  if (sent) {
    return (
      <AuthShell title="Check your e-mail" subtitle="If that address exists, a reset link is on its way.">
        <Link to="/login" className="text-sm font-medium text-accent hover:underline">
          Back to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset your password" subtitle="We'll e-mail you a link to set a new one.">
      <form onSubmit={requestReset} className="space-y-4">
        <Field label="E-mail">
          <Input type="email" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
        <Button type="submit" loading={busy} className="w-full">
          Send reset link
        </Button>
      </form>
      <Link to="/login" className="mt-5 inline-block text-sm text-muted hover:text-ink">
        Back to sign in
      </Link>
    </AuthShell>
  );
}
